import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Info, Laptop, Loader2, Mail } from "lucide-react";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type Session = {
  user: { id: string; email: string } | null;
  csrfToken: string | null;
};

type BillingInfo = {
  plan: "free" | "pro";
  billingState: string;
  stripeStatus: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  graceEndsAt: string | null;
  hasCustomer: boolean;
  desiredResources: {
    cpuKind: string;
    cpus: number;
    memoryMb: number;
    minimumVolumeSizeGb: number;
  };
  resourceStatus: string | null;
};

const managedStatuses = new Set(["active", "trialing", "past_due", "unpaid"]);

export function Billing() {
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState<Session | null>(null);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<"checkout" | "portal" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const sessionResponse = await fetch("/api/session");
      if (!sessionResponse.ok) {
        window.location.href = "/login";
        return;
      }
      const nextSession = (await sessionResponse.json()) as Session;
      setSession(nextSession);

      const billingResponse = await fetch("/api/billing");
      if (billingResponse.ok) {
        const data = (await billingResponse.json()) as { billing: BillingInfo };
        setBilling(data.billing);
      } else {
        setError("We couldn't load your billing details.");
      }
      setLoading(false);
    })();
  }, []);

  async function openStripe(endpoint: "checkout" | "portal") {
    if (!session?.csrfToken || action) return;
    setAction(endpoint);
    setError(null);
    try {
      const response = await fetch(`/api/billing/${endpoint}`, {
        method: "POST",
        headers: { "X-CSRF-Token": session.csrfToken },
      });
      const data = (await response.json()) as { url?: string; message?: string };
      if (!response.ok || !data.url) {
        throw new Error(data.message ?? "Stripe is unavailable right now.");
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stripe is unavailable right now.");
      setAction(null);
    }
  }

  const hasSubscription = billing ? managedStatuses.has(billing.stripeStatus) : false;
  const periodEnd = billing?.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd).toLocaleDateString([], {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const graceEnd = billing?.graceEndsAt
    ? new Date(billing.graceEndsAt).toLocaleString([], {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
  const resourceBusy = ["pending", "provisioning", "resizing", "retry"].includes(
    billing?.resourceStatus ?? ""
  );

  let stateCopy = "You're currently on Free. Upgrade whenever you need more room to do more.";
  if (billing?.billingState === "canceling" && periodEnd) {
    stateCopy = `Pro remains active through ${periodEnd}. You'll then return to Free without losing your files.`;
  } else if (billing?.billingState === "past_due_grace" && graceEnd) {
    stateCopy = `Your payment is past due. Pro remains active until ${graceEnd}. Update your payment method to keep Pro.`;
  } else if (billing?.billingState === "past_due") {
    stateCopy = "Your payment grace period ended. Free is active; update payment to restore Pro.";
  } else if (billing?.plan === "pro") {
    stateCopy = periodEnd ? `Pro is active and renews on ${periodEnd}.` : "Pro is active.";
  }
  if (resourceBusy) {
    stateCopy =
      billing?.resourceStatus === "retry"
        ? "Flock is retrying your computer update. Your files and credentials remain safe."
        : `${stateCopy} Your upgrade is being applied now.`;
  }

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      <header className="h-16 border-b border-zinc-200/70 bg-zinc-50/80 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link
            to="/"
            className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60"
          >
            <Logo showWordmark />
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm font-medium text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Dashboard
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-medium text-zinc-500">Billing</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Choose your computer.</h1>
          <p className="mt-3 text-zinc-600">
            Start with Flock Pro, or talk to us about a dedicated MacBook for work that needs macOS.
          </p>
        </div>

        {searchParams.get("checkout") === "success" && (
          <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Payment received. Stripe may take a moment to update your subscription status.
          </div>
        )}
        {searchParams.get("checkout") === "canceled" && (
          <div className="mt-8 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-600">
            Checkout was canceled. Nothing was charged.
          </div>
        )}

        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          <Card className="overflow-hidden border-zinc-200 bg-white shadow-sm">
            <CardContent className="flex h-full flex-col p-6 sm:p-8">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight">Flock Pro</h2>
                {hasSubscription && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium capitalize text-emerald-700">
                    {billing?.plan === "pro" ? "Pro" : billing?.billingState.replaceAll("_", " ")}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-zinc-500">
                A capable cloud computer for everyday work.
              </p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-3xl font-semibold tracking-tight">$25</span>
                <span className="text-sm text-zinc-500">/ month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-600">
                <li className="flex items-center gap-2">
                  <Check size={15} className="shrink-0 text-emerald-600" />
                  <span>More memory and power</span>
                  <span className="group relative inline-flex">
                    <button
                      type="button"
                      aria-label="Learn about additional memory and power"
                      aria-describedby="pro-power-info"
                      className="rounded-full text-zinc-400 outline-none transition-colors hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400/60"
                    >
                      <Info size={14} />
                    </button>
                    <span
                      id="pro-power-info"
                      role="tooltip"
                      className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-56 -translate-x-1/2 rounded-lg bg-zinc-900 px-3 py-2 text-xs leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                    >
                      Take on bigger projects and keep more work moving at once.
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>A dedicated workspace you can customize with the tools you need</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>Chrome included for online tasks, even while you're away</span>
                </li>
              </ul>
              {!loading && (
                <p className="mt-6 text-sm leading-relaxed text-zinc-500">{stateCopy}</p>
              )}
              <div className="mt-auto pt-8">
                {loading ? (
                  <div className="flex h-9 items-center text-sm text-zinc-500">
                    <Loader2 size={15} className="mr-2 animate-spin" />
                    Loading…
                  </div>
                ) : hasSubscription ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-zinc-300"
                    disabled={action !== null}
                    onClick={() => void openStripe("portal")}
                  >
                    {action === "portal" ? (
                      <Loader2 size={15} className="mr-2 animate-spin" />
                    ) : (
                      <ExternalLink size={15} className="mr-2" />
                    )}
                    Manage billing
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="w-full bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                    disabled={action !== null}
                    onClick={() => void openStripe("checkout")}
                  >
                    {action === "checkout" && <Loader2 size={15} className="mr-2 animate-spin" />}
                    Subscribe
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-zinc-800 bg-zinc-950 text-white shadow-xl shadow-zinc-950/10">
            <div className="absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-gradient-to-bl from-amber-300/20 to-transparent" />
            <CardContent className="relative flex h-full flex-col p-6 sm:p-8">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-amber-200">
                    <Laptop size={18} />
                  </span>
                  <h2 className="text-xl font-semibold tracking-tight">Flock MacBook</h2>
                </div>
                <span className="rounded-full border border-amber-200/20 bg-amber-200/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-amber-100">
                  Dedicated
                </span>
              </div>
              <p className="mt-4 text-sm text-zinc-400">
                Your own always-on MacBook, ready wherever you are.
              </p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-3xl font-semibold tracking-tight">$300</span>
                <span className="text-sm text-zinc-400">/ month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                <li className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-amber-300" />
                  <span>Real Apple silicon dedicated to you</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-amber-300" />
                  <span>Use macOS apps and tools from anywhere</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-amber-300" />
                  <span>Your files and setup stay ready between sessions</span>
                </li>
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-zinc-400">
                MacBook availability is confirmed with you before billing begins.
              </p>
              <div className="mt-auto pt-8">
                <a
                  href="mailto:hello@flock.build?subject=Flock%20MacBook%20tier"
                  className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-white px-2.5 text-sm font-medium whitespace-nowrap text-zinc-950 transition-colors outline-none hover:bg-amber-50 focus-visible:ring-3 focus-visible:ring-amber-200/40 active:translate-y-px"
                >
                  <Mail size={15} />
                  Contact us
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <p className="mt-6 text-xs leading-relaxed text-zinc-400">
          Payments are processed by Stripe. Flock does not store your card details.
        </p>
      </main>
    </div>
  );
}
